import React from 'react';
import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';
import { buildMonthGridCells, isSameCalendarDay, eventOverlapsLocalDay } from '../../utils/calendarUtils';

const WEEK_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MAX_CHIPS = 3;

const MonthCalendarGrid = ({
  viewYear,
  viewMonthIndex,
  events = [],
  selectedDate,
  onSelectDay,
  onAddOnDay,
  onEventClick,
  loading = false,
  getEventChipSx = null,
}) => {
  const theme = useTheme();
  const cells = buildMonthGridCells(viewYear, viewMonthIndex);
  const today = new Date();

  const eventsForDay = (dayDate) =>
    (events || []).filter((ev) => eventOverlapsLocalDay(ev, dayDate)).sort((a, b) => {
      const t1 = new Date(a.starts_at).getTime();
      const t2 = new Date(b.starts_at).getTime();
      return t1 - t2;
    });

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.6)',
            borderRadius: 1,
            pointerEvents: 'none',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Atualizando…
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 0,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {WEEK_LABELS.map((label) => (
          <Box
            key={label}
            sx={{
              py: 0.75,
              textAlign: 'center',
              bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {label}
            </Typography>
          </Box>
        ))}

        {cells.map((cellDate, idx) => {
          const inMonth = cellDate.getMonth() === viewMonthIndex;
          const isToday = isSameCalendarDay(cellDate, today);
          const selected = selectedDate && isSameCalendarDay(cellDate, selectedDate);
          const dayEvents = eventsForDay(cellDate);
          const overflow = dayEvents.length > MAX_CHIPS ? dayEvents.length - MAX_CHIPS : 0;
          const visible = dayEvents.slice(0, MAX_CHIPS);

          return (
            <Box
              key={`${cellDate.toISOString()}-${idx}`}
              onClick={() => onSelectDay?.(cellDate)}
              sx={{
                minHeight: { xs: 88, sm: 108, md: 120 },
                borderRight: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                p: 0.5,
                cursor: 'pointer',
                bgcolor: inMonth
                  ? theme.palette.background.paper
                  : theme.palette.mode === 'dark'
                    ? 'action.hover'
                    : 'grey.50',
                outline: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
                outlineOffset: -2,
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.25 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isToday ? theme.palette.primary.main : 'transparent',
                    color: isToday ? theme.palette.primary.contrastText : inMonth ? 'text.primary' : 'text.disabled',
                  }}
                >
                  <Typography variant="body2" fontWeight={isToday ? 700 : 500}>
                    {cellDate.getDate()}
                  </Typography>
                </Box>
                <Tooltip title="Novo compromisso neste dia">
                  <IconButton
                    size="small"
                    sx={{ p: 0.25 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddOnDay?.(cellDate);
                    }}
                  >
                    <AddIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ mt: 0.25, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {visible.map((ev) => {
                  const customSx = typeof getEventChipSx === 'function' ? getEventChipSx(ev) : null;
                  return (
                    <Chip
                      key={ev.id}
                      size="small"
                      label={ev.title}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick?.(ev);
                      }}
                      sx={{
                        height: 'auto',
                        py: 0.25,
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          display: 'block',
                          textAlign: 'left',
                          fontSize: '0.7rem',
                          lineHeight: 1.2,
                        },
                        maxWidth: '100%',
                        ...(customSx || {
                          bgcolor:
                            ev.visibility === 'public'
                              ? theme.palette.mode === 'dark'
                                ? 'primary.dark'
                                : 'primary.light'
                              : theme.palette.mode === 'dark'
                                ? 'grey.800'
                                : 'grey.200',
                        }),
                      }}
                    />
                  );
                })}
                {overflow > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 0.25 }}>
                    +{overflow} mais
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MonthCalendarGrid;
